import React, { useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SocketContext } from "../../../customComponents/SocketContext";
import { ProfileContext } from "../../../context/ProfileProvider";
import { resolvePublicUploadUrl } from "../../../api/apiConfig/apiConfig";
import AuthService from "../../../api/services/AuthService";
import {
  formatPrice,
  displaySpotChangePercent,
  isPositiveChange,
  spotChangeNumber,
} from "../Market/marketFormatters";
import DashboardTopContent from "./DashboardTopContent";
import DashboardRightSidebar from "./DashboardRightSidebar";

const DASHBOARD_ROW_LIMIT = 12;
const HOT_BASE_ORDER = [
  "BTC",
  "ETH",
  "SOL",
  "BNB",
  "XRP",
  "DOGE",
  "ADA",
  "AVAX",
  "LINK",
  "DOT",
  "ARB",
  "OP",
];

function normSym(v) {
  return String(v ?? "").trim().toUpperCase();
}

function pairListingTimeMs(p) {
  const d = p?.createdAt || p?.created_at || p?.listedAt;
  const t = d ? Date.parse(d) : NaN;
  if (Number.isFinite(t)) return t;
  const id = String(p?._id || "");
  if (/^[a-f0-9]{24}$/i.test(id)) {
    return parseInt(id.slice(0, 8), 16) * 1000;
  }
  return 0;
}

function pairVolumeNumber(p) {
  const n = Number(p?.volume ?? p?.quote_volume ?? p?.base_volume ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/** Pick the best USDT-quoted pair for a base, falling back to USDC/BUSD/first. */
function pickPairForBase(rows, base) {
  const b = normSym(base);
  const matches = rows.filter((p) => normSym(p?.base_currency) === b);
  if (!matches.length) return null;
  const preferQuotes = ["USDT", "USDC", "BUSD"];
  for (const q of preferQuotes) {
    const hit = matches.find((p) => normSym(p?.quote_currency) === q);
    if (hit) return hit;
  }
  return matches[0];
}

function getCoinName(item) {
  return (
    item?.base_currency_fullname ||
    item?.name ||
    item?.base_currency_f_name ||
    item?.coin_name ||
    item?.base_currency ||
    "—"
  );
}

function getCoinIcon(item) {
  return item?.icon_path
    ? `${resolvePublicUploadUrl(item.icon_path)}`
    : "/images/AnnouncementImg/activities_icon.png";
}

function formatHigh(item) {
  return formatPrice(item?.high ?? item?.buy_price, item?.tick_size);
}

/** Desktop table row: Coin | Price | 24H High | 24H Change | Trade */
function DashboardMarketRow({ item, onTrade }) {
  const pos = isPositiveChange(item);
  const chClass = pos ? "green" : "red";
  const sym = item?.base_currency || "—";
  const quote = item?.quote_currency || "USDT";
  return (
    <tr>
      <td>
        <div className="td_first">
          <div className="icon">
            <img
              src={getCoinIcon(item)}
              height="30px"
              alt={sym}
              onError={(e) => {
                e.target.src = "/images/AnnouncementImg/activities_icon.png";
              }}
            />
          </div>
          <div className="price_heading">{sym}</div>
        </div>
      </td>
      <td>{formatPrice(item?.buy_price, item?.tick_size)}</td>
      <td>{formatHigh(item)}</td>
      <td className={chClass}>{displaySpotChangePercent(item)}</td>
      <td className="right_t">
        <Link
          to={`/trade/${sym}_${quote}`}
          onClick={(e) => {
            e.stopPropagation();
            onTrade?.(item);
          }}
        >
          Trade
        </Link>
      </td>
    </tr>
  );
}

/** Mobile table row: Coin | Price | 24H High + Change */
function DashboardMarketRowMobile({ item, onTrade }) {
  const pos = isPositiveChange(item);
  const chClass = pos ? "green" : "red";
  const sym = item?.base_currency || "—";
  const quote = item?.quote_currency || "USDT";
  return (
    <tr
      onClick={() => onTrade?.(item)}
      style={{ cursor: "pointer" }}
    >
      <td>
        <div className="td_first">
          <div className="icon">
            <img
              src={getCoinIcon(item)}
              height="30px"
              alt={sym}
              onError={(e) => {
                e.target.src = "/images/AnnouncementImg/activities_icon.png";
              }}
            />
          </div>
          <div className="price_heading">
            {sym} <br />
            <span>{getCoinName(item)}</span>
          </div>
        </div>
      </td>
      <td>
        {formatPrice(item?.buy_price, item?.tick_size)} <br />
        <span className="fontWeight">{quote}</span>
      </td>
      <td className="right_t">
        {formatHigh(item)}
        <div className={chClass}>{displaySpotChangePercent(item)}</div>
      </td>
    </tr>
  );
}

function EmptyMarketRow({ colSpan, message }) {
  return (
    <tr className="no-hover">
      <td colSpan={colSpan} className="p-0">
        <div className="favouriteData text-center py-4">
          <img
            src="/images/no_data_vector.svg"
            className="img-fluid dark_img"
            width="96"
            height="96"
            alt="No data"
          />
          <img
            src="/images/no_data_vector_light.png"
            className="img-fluid light_img"
            width="96"
            height="96"
            alt="No data"
          />
          {message ? <p className="mt-2 mb-0">{message}</p> : null}
        </div>
      </td>
    </tr>
  );
}

/** Renders both desktop and mobile market tables for a tab with live rows. */
function MarketTabTable({ rows, onTrade, emptyMessage }) {
  return (
    <>
      <div className="desktop_view">
        <div className="table-responsive">
          <table>
            <thead>
              <tr>
                <th>Coin</th>
                <th>Price</th>
                <th>24H High</th>
                <th>24H Change</th>
                <th className="right_t">Trade</th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? (
                rows.map((item, i) => (
                  <DashboardMarketRow
                    key={item?._id || `${item?.base_currency}_${item?.quote_currency}_${i}`}
                    item={item}
                    onTrade={onTrade}
                  />
                ))
              ) : (
                <EmptyMarketRow colSpan={5} message={emptyMessage} />
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="mobile_view">
        <div className="table-responsive">
          <table>
            <thead>
              <tr>
                <th>Coin</th>
                <th>Price</th>
                <th className="right_t">24 High/Change</th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? (
                rows.map((item, i) => (
                  <DashboardMarketRowMobile
                    key={item?._id || `${item?.base_currency}_${item?.quote_currency}_${i}`}
                    item={item}
                    onTrade={onTrade}
                  />
                ))
              ) : (
                <EmptyMarketRow colSpan={3} message={emptyMessage} />
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

const Dashboard = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { socket, marketData, subscribeToMarket, unsubscribeFromMarket } = useContext(SocketContext);
  const tabsRef = useRef(null);
  const [showArrows, setShowArrows] = useState({ left: false, right: false });

  const checkScroll = useCallback(() => {
    if (tabsRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabsRef.current;
      setShowArrows({
        left: scrollLeft > 0,
        right: scrollLeft + clientWidth < scrollWidth - 1,
      });
    }
  }, []);

  useEffect(() => {
    // Check initial scroll once everything renders
    const timer = setTimeout(checkScroll, 100);
    window.addEventListener("resize", checkScroll);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", checkScroll);
    };
  }, [checkScroll]);

  const scrollTabs = (direction) => {
    if (tabsRef.current) {
      const scrollAmount = 150;
      tabsRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };
  const { userDetails } = useContext(ProfileContext);

  const [favCoins, setFavCoins] = useState([]);

  const coinData = useMemo(
    () => (Array.isArray(marketData?.pairs) ? marketData.pairs : []),
    [marketData]
  );

  useEffect(() => {
    if (socket) {
      subscribeToMarket();
      return () => unsubscribeFromMarket();
    }
  }, [socket, subscribeToMarket, unsubscribeFromMarket]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const result = await AuthService.favoriteList();
        if (result?.success) {
          setFavCoins(result?.data?.pairs || []);
        }
      } catch {
        // Silently ignore — favorites are a progressive enhancement.
      }
    })();
  }, [token]);

  /** USDT-quoted universe (preferred base set) with fallback to all pairs when empty. */
  const spotUsdtPairs = useMemo(() => {
    if (!coinData.length) return [];
    const usdt = coinData.filter((p) => normSym(p?.quote_currency) === "USDT");
    return usdt.length ? usdt : coinData;
  }, [coinData]);

  /** Trending: highest 24h volume among USDT spot pairs. */
  const trendingRows = useMemo(() => {
    if (!spotUsdtPairs.length) return [];
    return [...spotUsdtPairs]
      .sort((a, b) => pairVolumeNumber(b) - pairVolumeNumber(a))
      .slice(0, DASHBOARD_ROW_LIMIT);
  }, [spotUsdtPairs]);

  /** Hot: curated majors preferring USDT quote; falls back to the trending list. */
  const hotRows = useMemo(() => {
    if (!coinData.length) return [];
    const seen = new Set();
    const out = [];
    for (const base of HOT_BASE_ORDER) {
      const p = pickPairForBase(coinData, base);
      if (p && !seen.has(p._id)) {
        seen.add(p._id);
        out.push(p);
      }
    }
    if (out.length >= DASHBOARD_ROW_LIMIT) return out.slice(0, DASHBOARD_ROW_LIMIT);
    for (const p of spotUsdtPairs) {
      if (out.length >= DASHBOARD_ROW_LIMIT) break;
      if (!p?._id || seen.has(p._id)) continue;
      seen.add(p._id);
      out.push(p);
    }
    return out;
  }, [coinData, spotUsdtPairs]);

  /** New Listing: newest pairs by createdAt / listing time (falls back to ObjectId ordering). */
  const newListingRows = useMemo(() => {
    if (!spotUsdtPairs.length) return [];
    return [...spotUsdtPairs]
      .sort((a, b) => pairListingTimeMs(b) - pairListingTimeMs(a))
      .slice(0, DASHBOARD_ROW_LIMIT);
  }, [spotUsdtPairs]);

  /** Top Gainers: highest positive 24h change. */
  const topGainersRows = useMemo(() => {
    if (!spotUsdtPairs.length) return [];
    return [...spotUsdtPairs]
      .sort((a, b) => spotChangeNumber(b) - spotChangeNumber(a))
      .slice(0, DASHBOARD_ROW_LIMIT);
  }, [spotUsdtPairs]);

  /** Favorites: user-saved pairs (requires login). */
  const favoriteRows = useMemo(() => {
    if (!token || !coinData.length || !favCoins.length) return [];
    return coinData.filter((p) => p?._id && favCoins.includes(p._id));
  }, [coinData, favCoins, token]);

  const goToTrade = useCallback(
    (data) => {
      if (!data?.base_currency || !data?.quote_currency) return;
      try {
        localStorage.setItem("RecentPair", JSON.stringify(data));
      } catch {
        // Ignore storage quota errors.
      }
      navigate(`/trade/${data.base_currency}_${data.quote_currency}`);
    },
    [navigate]
  );

  const favoriteEmptyMessage = token
    ? "You haven't favorited any pairs yet."
    : "Login to view your favorite pairs.";

  // Keep legacy sample wallet snapshot masked toggle untouched — snapshot is still static.
  return (
    <>
      <div className="dashboard_right">

        <div className="dashboard_listing_section maindash">

          <div className="dashboard_left_side">
            <DashboardTopContent userDetails={userDetails} />

            <div className="listing_left_outer">
              {/* <div className="crypto_deposit">
            <h4>Start by depositing some crypto</h4>
            <ul>
              <li>
                <div className="estimate_cnt">
                  <h5>Estimated Portfolio</h5>
                  <h4>
                    12456.78901234 USD{" "}
                    <span>
                      1023456.78 INR
                    </span>
                    <i className="ri-eye-close-line mx-1" />
                  </h4>
                </div>
                <div className="estimated_portfolio">
                  <Link className="deposit_btn" to="/asset_managemnet/deposit">Deposit</Link>
                  <Link className="deposit_btn withdraw" to="/asset_managemnet/withdraw">Withdraw</Link>
                </div>
              </li>
            </ul>
          </div> */}

              <div className="market_section maindashboard desk_dashordlist_mobile">
                <div className="top_heading">
                  <h4>Spot Markets</h4>
                  <Link className="more_btn" to="/market"><i className="ri-arrow-right-s-line"></i></Link>
                </div>
                <div className="dashboard_summary">
                  <div className="tabs_scroll_wrapper d-flex align-items-center position-relative w-100">
                    {showArrows.left && (
                      <button className="tab_scroll_btn left_btn" onClick={() => scrollTabs('left')}><i className="ri-arrow-left-s-line"></i></button>
                    )}
                    <ul className="nav nav-tabs" id="myTab" role="tablist" ref={tabsRef} onScroll={checkScroll}>
                      <li className="nav-item" role="presentation">
                        <button className="nav-link" id="favorite-tab" data-bs-toggle="tab" data-bs-target="#favorite" type="button" role="tab" aria-controls="favorite" aria-selected="false">Favorite</button>
                      </li>
                      <li className="nav-item" role="presentation">
                        <button className="nav-link active" id="home-tab" data-bs-toggle="tab" data-bs-target="#home" type="button" role="tab" aria-controls="home" aria-selected="true">Trending</button>
                      </li>
                      <li className="nav-item" role="presentation">
                        <button className="nav-link" id="profile-tab" data-bs-toggle="tab" data-bs-target="#profile" type="button" role="tab" aria-controls="profile" aria-selected="false">Hot</button>
                      </li>
                      <li className="nav-item" role="presentation">
                        <button className="nav-link" id="contact-tab" data-bs-toggle="tab" data-bs-target="#contact" type="button" role="tab" aria-controls="contact" aria-selected="false">New Listing</button>
                      </li>
                      <li className="nav-item" role="presentation">
                        <button className="nav-link" id="gainers-tab" data-bs-toggle="tab" data-bs-target="#gainers" type="button" role="tab" aria-controls="gainers" aria-selected="false">Top Gainers</button>
                      </li>
                    </ul>
                    {showArrows.right && (
                      <button className="tab_scroll_btn right_btn" onClick={() => scrollTabs('right')}><i className="ri-arrow-right-s-line"></i></button>
                    )}
                  </div>

                  <div className="tab-content" id="myTabContent">
                    <div className="tab-pane fade show active" id="home" role="tabpanel" aria-labelledby="home-tab">
                      <MarketTabTable
                        rows={trendingRows}
                        onTrade={goToTrade}
                        emptyMessage="Loading markets…"
                      />
                    </div>

                    <div className="tab-pane fade" id="profile" role="tabpanel" aria-labelledby="profile-tab">
                      <MarketTabTable
                        rows={hotRows}
                        onTrade={goToTrade}
                        emptyMessage="Loading markets…"
                      />
                    </div>

                    <div className="tab-pane fade" id="contact" role="tabpanel" aria-labelledby="contact-tab">
                      <MarketTabTable
                        rows={newListingRows}
                        onTrade={goToTrade}
                        emptyMessage="Loading markets…"
                      />
                    </div>

                    <div className="tab-pane fade" id="favorite" role="tabpanel" aria-labelledby="favorite-tab">
                      <MarketTabTable
                        rows={favoriteRows}
                        onTrade={goToTrade}
                        emptyMessage={favoriteEmptyMessage}
                      />
                    </div>

                    <div className="tab-pane fade" id="gainers" role="tabpanel" aria-labelledby="gainers-tab">
                      <MarketTabTable
                        rows={topGainersRows}
                        onTrade={goToTrade}
                        emptyMessage="Loading markets…"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <DashboardRightSidebar userDetails={userDetails} />
        </div>

        <div className="market_section maindashboard desk_dashordlist">
          <div className="top_heading">
            <h4>Spot Markets</h4>
            <Link className="more_btn" to="/market"><i className="ri-arrow-right-s-line"></i></Link>
          </div>
          <div className="dashboard_summary">
            <div className="tabs_scroll_wrapper d-flex align-items-center position-relative w-100">
              {showArrows.left && (
                <button className="tab_scroll_btn left_btn" onClick={() => scrollTabs('left')}><i className="ri-arrow-left-s-line"></i></button>
              )}
              <ul className="nav nav-tabs" id="myTab" role="tablist" ref={tabsRef} onScroll={checkScroll}>
                <li className="nav-item" role="presentation">
                  <button className="nav-link" id="favorite-tab" data-bs-toggle="tab" data-bs-target="#favorite" type="button" role="tab" aria-controls="favorite" aria-selected="false">Favorite</button>
                </li>
                <li className="nav-item" role="presentation">
                  <button className="nav-link active" id="home-tab" data-bs-toggle="tab" data-bs-target="#home" type="button" role="tab" aria-controls="home" aria-selected="true">Trending</button>
                </li>
                <li className="nav-item" role="presentation">
                  <button className="nav-link" id="profile-tab" data-bs-toggle="tab" data-bs-target="#profile" type="button" role="tab" aria-controls="profile" aria-selected="false">Hot</button>
                </li>
                <li className="nav-item" role="presentation">
                  <button className="nav-link" id="contact-tab" data-bs-toggle="tab" data-bs-target="#contact" type="button" role="tab" aria-controls="contact" aria-selected="false">New Listing</button>
                </li>
                <li className="nav-item" role="presentation">
                  <button className="nav-link" id="gainers-tab" data-bs-toggle="tab" data-bs-target="#gainers" type="button" role="tab" aria-controls="gainers" aria-selected="false">Top Gainers</button>
                </li>
              </ul>
              {showArrows.right && (
                <button className="tab_scroll_btn right_btn" onClick={() => scrollTabs('right')}><i className="ri-arrow-right-s-line"></i></button>
              )}
            </div>

            <div className="tab-content" id="myTabContent">
              <div className="tab-pane fade show active" id="home" role="tabpanel" aria-labelledby="home-tab">
                <MarketTabTable
                  rows={trendingRows}
                  onTrade={goToTrade}
                  emptyMessage="Loading markets…"
                />
              </div>

              <div className="tab-pane fade" id="profile" role="tabpanel" aria-labelledby="profile-tab">
                <MarketTabTable
                  rows={hotRows}
                  onTrade={goToTrade}
                  emptyMessage="Loading markets…"
                />
              </div>

              <div className="tab-pane fade" id="contact" role="tabpanel" aria-labelledby="contact-tab">
                <MarketTabTable
                  rows={newListingRows}
                  onTrade={goToTrade}
                  emptyMessage="Loading markets…"
                />
              </div>

              <div className="tab-pane fade" id="favorite" role="tabpanel" aria-labelledby="favorite-tab">
                <MarketTabTable
                  rows={favoriteRows}
                  onTrade={goToTrade}
                  emptyMessage={favoriteEmptyMessage}
                />
              </div>

              <div className="tab-pane fade" id="gainers" role="tabpanel" aria-labelledby="gainers-tab">
                <MarketTabTable
                  rows={topGainersRows}
                  onTrade={goToTrade}
                  emptyMessage="Loading markets…"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
